import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { getAuthToken } from '../../auth/context/AuthContext';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/platform`;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin:       'Admin',
  manager:     'Manager',
  team_lead:   'Team Lead',
  recruiter:   'Recruiter',
};

const ROLE_ORDER = ['super_admin', 'admin', 'manager', 'team_lead', 'recruiter'];

function Stat({ label, value, sub, accent }) {
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: accent || 'var(--text-primary)', lineHeight: 1.15, marginTop: 6 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function PlatformDashboard() {
  const [s, setS]           = useState(null);
  const [loading, setLoad]  = useState(true);
  const [err, setErr]       = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/stats`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || `Failed to load stats (${res.status})`);
        }
        setS(await res.json());
      } catch (e) { setErr(e.message); }
      setLoad(false);
    })();
  }, []);

  if (loading) return <div className="page"><div className="empty-state"><div className="empty-state-text">Loading…</div></div></div>;
  if (err)     return <div className="page"><div className="alert alert-error" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={14} strokeWidth={2} /> {err}</div></div>;

  const roles = ROLE_ORDER.filter(r => s.users_by_role?.[r]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Platform Overview</div>
          <div className="page-subtitle">Recruiter24x7 across all customer companies</div>
        </div>
        <Link to="/platform/companies" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          Manage Companies <ArrowRight size={14} strokeWidth={2} />
        </Link>
      </div>

      {s.companies_without_admin?.length > 0 && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          <AlertTriangle size={14} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> {s.companies_without_admin.length} compan{s.companies_without_admin.length === 1 ? 'y has' : 'ies have'} no
          active admin: {s.companies_without_admin.join(', ')} — nobody can manage their team.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 22 }}>
        <Stat
          label="Companies"
          value={s.companies_total}
          sub={`${s.companies_active} active · ${s.companies_inactive} inactive`}
          accent="var(--accent)"
        />
        <Stat
          label="Users"
          value={s.users_total}
          sub={`${s.users_active} active`}
        />
        <Stat label="Job Posts" value={s.jobs_total} sub="across all companies" />
        <Stat label="Candidates" value={s.candidates_total} sub="across all companies" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Users by role</div>
          {roles.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No users yet.</div>
          ) : roles.map(r => {
            const n = s.users_by_role[r];
            const pct = s.users_total ? Math.round((n / s.users_total) * 100) : 0;
            return (
              <div key={r} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>{ROLE_LABELS[r] || r}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{n}</span>
                </div>
                <div style={{ height: 6, borderRadius: 999, background: 'rgba(127,127,127,0.15)', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)' }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Most active companies</div>
          {s.top_companies?.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No companies yet.</div>
          ) : (
            <div className="table-scroll">
            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr><th>Company</th><th style={{ textAlign: 'right' }}>Users</th><th style={{ textAlign: 'right' }}>Jobs</th></tr>
              </thead>
              <tbody>
                {s.top_companies.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>
                      {c.name}
                      {!c.is_active && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}> (inactive)</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{c.user_count}</td>
                    <td style={{ textAlign: 'right' }}>{c.job_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
